import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';
import type { DeskAnalysis, DeskSource, SectorDesk } from 'api/sectorAlignmentApi';
import { SPINNER } from './DeskCard';
import { sourceLabel } from './deskSelection';
import type { DeskView } from './pageState';

export interface MobileDesk {
  sd: SectorDesk;
  name: string;
  source: DeskSource | null;
  result?: DeskAnalysis;
  view: DeskView;
}

interface MobileDeskListProps {
  desks: MobileDesk[];
  renderCard: (sd: SectorDesk) => ReactNode;
}

/** §1.8 (375 px): one compact row per desk; a row unfolds its full DeskCard inline (R9, AC23). */
const MobileDeskList = ({ desks, renderCard }: MobileDeskListProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<SectorDesk | null>(null);
  const toRealign = desks.filter((d) => d.result?.status === 'ok' && d.result.misaligned > 0);
  const tickets = toRealign.reduce((sum, d) => sum + (Number(d.result?.misaligned) || 0), 0);

  return (
    <div className="flex flex-col gap-3.5 md:hidden">
      <div className="overflow-hidden rounded-[13px] border bg-card">
        {desks.map(({ sd, name, source, result, view }) => {
          const expanded = open === sd;
          const reading = view === 'skeleton' || view === 'refreshing';
          const ok = !reading && result?.status === 'ok' ? result : null;
          return (
            <div key={sd} className="border-b last:border-b-0">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : sd)}
                className="flex min-h-[60px] w-full items-center gap-3 px-3.5 py-2.5 text-left"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-semibold">{name}</span>
                  {source && (
                    <span className="text-xs text-muted-foreground">{sourceLabel(t, source)}</span>
                  )}
                </span>
                {reading && (
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={SPINNER} aria-hidden="true" />
                    {t('Analysis in progress…')}
                  </span>
                )}
                {view === 'pending' && <span className="text-xs text-muted-foreground">{t('Waiting for analysis')}</span>}
                {!reading && result?.status === 'error' && (
                  <span className="flex items-center gap-1 text-[12.5px] font-medium text-rose-700 dark:text-rose-400">
                    <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('Error')}
                  </span>
                )}
                {ok && ok.misaligned > 0 && (
                  <span className="flex flex-col items-end">
                    <span className="text-lg font-semibold tabular-nums">{ok.misaligned}</span>
                    <span className="text-[11.5px] text-muted-foreground">{t('misaligned', { count: ok.misaligned })}</span>
                  </span>
                )}
                {ok && ok.misaligned <= 0 && (
                  <span className="flex items-center gap-1 text-[12.5px] font-medium text-emerald-700 dark:text-emerald-400">
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('Aligned')}
                  </span>
                )}
                <ChevronRight className={cn('h-[15px] w-[15px] shrink-0 text-muted-foreground transition-transform', expanded && 'rotate-90')} aria-hidden="true" />
              </button>
              {expanded && <div className="px-2 pb-2">{renderCard(sd)}</div>}
            </div>
          );
        })}
        {toRealign.length > 0 && (
          <p className="px-3.5 py-2.5 text-xs text-muted-foreground">
            {t('{{count}} desks to realign', { count: toRealign.length })} · {t('{{count}} tickets', { count: tickets })}
          </p>
        )}
      </div>
      <p className="text-xs text-muted-foreground">{t('Open a desk to see its stores and realign it.')}</p>
    </div>
  );
};

export default MobileDeskList;
